import { Upload } from '@runelab/odin-common'
import { saveAs } from 'file-saver'
import { useState } from 'react'
import { FileRow } from 'components/ui/FileRow'
import { ChatAttachment } from 'types/chat-attachment'
import { useChatScroll } from 'components/chats/chatScrollContext'

// Single normalized model rendered by AttachmentMessage, fed from either an operator `Upload`
// or a bot `ChatAttachment`.
export type MessageAttachment = {
  name: string
  url?: string
  mimeType?: string
}

export const fromUpload = (upload: Upload): MessageAttachment => ({
  name: upload.additionalInfo?.fullName ?? upload.originalName ?? upload.full ?? 'File',
  url: upload.full,
})

export const fromChatAttachment = (attachment: ChatAttachment): MessageAttachment => ({
  name: attachment.name,
  url: attachment.url,
  mimeType: attachment.mimeType,
})

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp']

const isImageAttachment = (attachment: MessageAttachment): boolean => {
  if (attachment.mimeType) return attachment.mimeType.startsWith('image/')
  const ext = attachment.name.split('.').pop()?.toLowerCase() || ''
  return IMAGE_EXTENSIONS.includes(ext)
}

export type AttachmentMessageProps = {
  attachment: MessageAttachment
  align?: 'left' | 'right'
  borderClass?: string
}

export const AttachmentMessage = ({ attachment, align = 'left', borderClass }: AttachmentMessageProps) => {
  const [imageError, setImageError] = useState(false)
  const { scrollToBottom } = useChatScroll()

  const isLeft = align === 'left'
  // Left attachments line up with the bubble column (avatar + gap); right ones hug the edge.
  const indentClass = isLeft ? 'tw-pl-11' : 'tw-pl-5'
  const justifyClass = isLeft ? 'tw-justify-start' : 'tw-justify-end'
  const isImage = isImageAttachment(attachment) && !imageError

  const download = async () => {
    try {
      if (!attachment.url) return
      const response = await fetch(attachment.url)
      saveAs(await response.blob(), attachment.name)
    } catch (e) {
      console.error({ e })
    }
  }

  return (
    <div className={`animate-fade-slide-in tw-flex ${indentClass} ${justifyClass}`}>
      <div className="tw-max-w-[280px]">
        {isImage && attachment.url ? (
          <div className={`tw-cursor-pointer tw-overflow-hidden ${borderClass || 'tw-rounded-lg'}`} onClick={download}>
            <img
              src={attachment.url}
              alt={attachment.name}
              className="tw-h-auto tw-w-full tw-object-cover"
              onLoad={scrollToBottom}
              onError={() => setImageError(true)}
            />
          </div>
        ) : (
          <FileRow fileName={attachment.name} onClick={download} dark={!isLeft} />
        )}
      </div>
    </div>
  )
}
